import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Form,
  InternalPage,
  ResetFormHandler,
  TextField,
} from 'theSource/components';
import { ErrorResponseDto } from 'theSource/types';
import { useApiMutation } from 'theSource/utils';
import { IdCardAddUpload } from './components';
import {
  IdCardAddFormValues,
  IdCardPostRequestDto,
  IdCardRequest,
} from './types';
import {
  EMPTY_ID_CARD,
  getValidationSchema,
  INITIAL_ID_CARD_ADD_FORM_VALUES,
  mapIdCardAddDto,
  POST_ID_CARD_QUERY_KEY,
} from './utils';
import { GET_ID_CARDS_QUERY_KEY } from '../utils';
import { ROUTES } from 'theSource/app/routes';
import { IdCardsPageResponseDto } from '../types';
import { BackLink } from 'enrollment/components';

export const MyIdCardsAddNewPage = () => {
  const [idCard, setIdCard] = useState<IdCardRequest>(EMPTY_ID_CARD);

  const { t } = useTranslation('pages.idCards');
  const navigate = useNavigate();

  const navigateBack = () => navigate(ROUTES.main.idCards.path);

  const { isLoading: isAdding, mutate: addCard } = useApiMutation<
    IdCardsPageResponseDto,
    ErrorResponseDto,
    IdCardPostRequestDto
  >({
    queryKey: POST_ID_CARD_QUERY_KEY,
    queryKeysToInvalidate: GET_ID_CARDS_QUERY_KEY,
    invalidateQueriesCallback: () => navigateBack(),
    method: 'post',
    endpoint: '/ecards',
    getErrorMessage: (error) => error.response?.data?.details,
  });

  const handleSubmit = (
    values: IdCardAddFormValues,
    resetForm: ResetFormHandler
  ) => {
    addCard(mapIdCardAddDto(idCard, values as IdCardAddFormValues), {
      onSuccess: () => resetForm(),
    });
  };

  const handleUpload = (name: string, url: string) => {
    switch (name) {
      case 'frontImage':
        setIdCard({ ...idCard, frontImage: url });
        break;
      case 'backImage':
        setIdCard({ ...idCard, backImage: url });
        break;
    }
  };

  return (
    <InternalPage
      testId="id-card-add"
      title={t('addIdCard')}
      CustomActionComponent={<BackLink />}
      customActionAlignment="left"
    >
      <Form<IdCardAddFormValues>
        initialValues={INITIAL_ID_CARD_ADD_FORM_VALUES}
        validationSchema={getValidationSchema()}
        onSubmitClick={handleSubmit}
        onCancelClick={navigateBack}
        isLoading={isAdding}
      >
        <TextField
          data-testid="card-name-input"
          required
          id="name"
          label={t('cardName')}
          name="name"
          placeholder={t('cardNamePlaceholder')}
        />
        <IdCardAddUpload
          testId="front-image-upload"
          required
          id="frontImage"
          label={t('front')}
          imgSrc={idCard.frontImage}
          alt="ID Card's front"
          onUpload={handleUpload}
        />
        <IdCardAddUpload
          testId="back-image-upload"
          id="backImage"
          label={t('back')}
          imgSrc={idCard.backImage}
          alt="ID Card's back"
          onUpload={handleUpload}
        />
      </Form>
    </InternalPage>
  );
};
